/**
 * OfferCheckout — dedicated pre-Stripe review screen for special offers.
 *
 * Special offers (Welcome / Daily / Weekly / Monthly) require the customer
 * to pick which game profile receives the Apex before hand-off to Stripe.
 * This page mirrors the standard `/checkout` flow's confirmation card:
 *   1. Game profile picker (or add-a-profile inline)
 *   2. Confirm Server ID / Governor UID / In-Game Name checkbox
 *   3. Terms + Refund Policy checkbox
 *   4. Immediate-fulfilment checkbox
 *
 * Only then do we POST to `/special-offers/{id}/purchase` — Stripe hand-off
 * (LIVE mode) or the TEST-MODE simulated order happens exactly as before.
 */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useAuth } from "../lib/auth";
import { useCurrency } from "../lib/currency";
import { useStoreStatus } from "../lib/storeStatus";
import { Sparkles, ShieldCheck, ArrowRight, Wallet, AlertTriangle } from "lucide-react";

const OFFER_LABEL = {
  welcome: "Welcome Offer",
  daily: "Daily Offer",
  weekly: "Weekly Offer",
  monthly: "Monthly Offer",
};

export default function OfferCheckout() {
  const { offerId } = useParams();
  const nav = useNavigate();
  const { user, loading } = useAuth();
  const { format, code, isBase } = useCurrency();
  const { salesPaused, pauseMessage } = useStoreStatus();

  const [offer, setOffer] = useState(null);
  const [liveMode, setLiveMode] = useState(true);
  const [profiles, setProfiles] = useState([]);
  const [profileId, setProfileId] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [newProfile, setNewProfile] = useState({ profile_name: "", server_id: "", governor_uid: "", in_game_name: "", is_default: true });
  const [confirmed, setConfirmed] = useState(false);
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [policyVersions, setPolicyVersions] = useState({ terms: null, refund: null });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/special-offers").then(r => {
      setLiveMode(!!r.data.live_mode);
      const found = (r.data.offers || []).find(o => o.id === offerId);
      if (!found) { setErr("Offer not available"); return; }
      if (found.eligible === false) { setErr("You're not eligible for this offer"); return; }
      if (found.remaining_in_period === 0) { setErr("Allowance used — try after the reset"); return; }
      setOffer(found);
    }).catch(() => setErr("Could not load offer"));
    if (user) {
      api.get("/game-profiles").then(r => {
        setProfiles(r.data);
        if (r.data.length) setProfileId(r.data.find(p => p.is_default)?.id || r.data[0].id);
      });
    }
    api.get("/policies/versions").then(r => setPolicyVersions(r.data.versions || {})).catch(() => {});
  }, [offerId, user]);

  const addProfile = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/game-profiles", newProfile);
      const list = await api.get("/game-profiles");
      setProfiles(list.data);
      setProfileId(data.id);
      setShowNew(false);
      toast.success("Game profile saved");
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Could not save profile");
    }
  };

  const pay = async () => {
    // Client-side + server-side fallback both route to the SPECIFIC missing
    // control instead of a dead-end toast, so the customer never sees a
    // "please agree" message without also being taken to the checkbox.
    const focusUnticked = () => {
      let cbSel = null, msg = "";
      if (!profileId) {
        const sel = document.querySelector('[data-testid="offer-profile-select"]') ||
                    document.querySelector('[data-testid="offer-add-profile-toggle"]');
        if (sel) { sel.scrollIntoView({ behavior: "smooth", block: "center" }); setTimeout(() => sel.focus(), 500); }
        toast.error("Please pick a game profile");
        return;
      }
      if (!confirmed) {
        cbSel = '[data-testid="offer-confirm-checkbox"]';
        msg = "Please tick the confirmation box to continue";
      } else if (!agreedTerms) {
        cbSel = '[data-testid="offer-terms-checkbox"]';
        msg = "Please agree to the Terms & Conditions and Refund Policy";
      }
      const cb = cbSel && document.querySelector(cbSel);
      const row = cb && cb.closest("label");
      if (row) {
        row.scrollIntoView({ behavior: "smooth", block: "center" });
        row.classList.add("bg-[rgba(212,175,55,0.14)]", "ring-2", "ring-[var(--av-gold)]", "rounded-md", "px-1");
        setTimeout(() =>
          row.classList.remove("bg-[rgba(212,175,55,0.14)]", "ring-2", "ring-[var(--av-gold)]", "rounded-md", "px-1"),
          2600);
      } else {
        const card = document.getElementById("offer-confirm-card");
        if (card) card.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      if (cb) setTimeout(() => cb.focus(), 500);
      if (msg) toast.error(msg);
    };
    if (!profileId || !confirmed || !agreedTerms) {
      focusUnticked();
      return;
    }
    setBusy(true);
    try {
      const { data: r } = await api.post(`/special-offers/${offerId}/purchase`, {
        origin_url: window.location.origin,
        payment_method: "card",
        game_profile_id: profileId,
        accepted_terms: true,
        accepted_refund_policy: true,
        accepted_terms_version: policyVersions.terms,
        accepted_refund_version: policyVersions.refund,
      });
      if (r.checkout_url) {
        window.location.href = r.checkout_url;
        return;
      }
      if (r.test_mode) {
        toast.success(`TEST ORDER — ${r.notice || "simulated end-to-end"}. Order ${r.order_id}.`);
      } else {
        toast.success(`Order placed: ${r.order_id}.`);
      }
      nav("/my-vault");
    } catch (e) {
      // Server-side fallback for policy errors — take them straight to the box.
      const detail = String(e?.response?.data?.detail || "");
      const low = detail.toLowerCase();
      if (low.includes("terms") || low.includes("refund")) {
        focusUnticked();
      } else {
        toast.error(detail || "Purchase failed");
      }
      setBusy(false);
    }
  };

  // ────────── Auth wall ──────────
  if (!loading && !user) return (
    <div>
      <Header/>
      <div className="max-w-md mx-auto pt-32 px-4 text-center">
        <div className="vault-card p-8">
          <h1 className="font-serif text-3xl font-bold gold-text mb-3">Sign in to claim</h1>
          <p className="text-[var(--av-text-dim)] mb-6">You need an Arthur's Vault account to buy special offers.</p>
          <div className="flex gap-3 justify-center">
            <Link to={`/auth?mode=register`} className="btn-gold" data-testid="offer-checkout-register">Create Account</Link>
            <Link to={`/auth?mode=login`} className="btn-outline-gold" data-testid="offer-checkout-login">Log In</Link>
          </div>
        </div>
      </div>
      <Footer/>
    </div>
  );

  if (err) return (
    <div>
      <Header/>
      <div className="max-w-md mx-auto pt-32 px-4 text-center">
        <div className="vault-card p-8">
          <div className="text-red-300 mb-2 flex items-center justify-center gap-2"><AlertTriangle className="w-4 h-4"/> {err}</div>
          <Link to="/" className="btn-outline-gold text-sm mt-4 inline-block">Back to Vault</Link>
        </div>
      </div>
      <Footer/>
    </div>
  );

  if (!offer) return <div><Header/><div className="pt-32 text-center text-[var(--av-text-dim)]">Loading…</div></div>;

  const offerName = OFFER_LABEL[offerId] || offer.name;
  const priceStr = format(offer.special_price_gbp);

  return (
    <div>
      <Header/>
      <main className="pt-28 pb-16 px-4 sm:px-8 max-w-5xl mx-auto min-w-0" data-testid="offer-checkout">
        <div className="mono-caption purple-text mb-2">Special Offer · Review</div>
        <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-2 leading-tight">
          <span className="gold-text">{offerName}</span>
        </h1>
        <p className="text-sm text-[var(--av-text-dim)] mb-6">Confirm your details, agree to the policies, then continue to secure Stripe checkout.</p>

        {!liveMode && (
          <div className="pill pill-purple text-[10px] flex items-center gap-1 mb-4 w-fit" data-testid="offer-checkout-test-mode">
            <AlertTriangle className="w-3 h-3"/> TEST MODE — no real payment
          </div>
        )}

        <div className="grid lg:grid-cols-5 gap-6 min-w-0">
          <div className="lg:col-span-3 space-y-6 min-w-0">
            {/* Game profile picker */}
            <div className="vault-card p-6">
              <div className="mono-caption mb-3">Game Profile</div>
              {profiles.length === 0 ? (
                <p className="text-sm text-[var(--av-text-dim)] mb-3">You don't have any game profiles yet. Add one to receive the Apex.</p>
              ) : (
                <select
                  data-testid="offer-profile-select"
                  value={profileId}
                  onChange={e => setProfileId(e.target.value)}
                  className="input-vault mb-3"
                >
                  {profiles.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.profile_name || p.in_game_name} · {p.server_id} · UID {p.governor_uid}
                    </option>
                  ))}
                </select>
              )}
              <button
                data-testid="offer-add-profile-toggle"
                onClick={() => setShowNew(v => !v)}
                className="btn-outline-gold text-xs"
              >
                {showNew ? "Cancel" : "+ Add a game profile"}
              </button>
              {showNew && (
                <form onSubmit={addProfile} className="mt-4 space-y-2">
                  <input required placeholder="Profile name (e.g. Main)" className="input-vault"
                          value={newProfile.profile_name}
                          onChange={e=>setNewProfile({...newProfile, profile_name: e.target.value})}/>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input required placeholder="Server ID (e.g. E75)" className="input-vault font-mono"
                            value={newProfile.server_id}
                            onChange={e=>setNewProfile({...newProfile, server_id: e.target.value.toUpperCase()})}/>
                    <input required placeholder="Governor UID" className="input-vault font-mono"
                            value={newProfile.governor_uid}
                            onChange={e=>setNewProfile({...newProfile, governor_uid: e.target.value})}/>
                  </div>
                  <input required placeholder="In-Game Name" className="input-vault"
                          value={newProfile.in_game_name}
                          onChange={e=>setNewProfile({...newProfile, in_game_name: e.target.value})}/>
                  <button data-testid="offer-save-profile-btn" className="btn-purple text-sm">Save profile</button>
                </form>
              )}
            </div>

            {/* Confirmation + legal */}
            {profileId && (() => {
              const p = profiles.find(x => x.id === profileId);
              if (!p) return null;
              return (
                <div id="offer-confirm-card" className="vault-card p-6 transition-shadow">
                  <div className="mono-caption mb-3">Confirm Your Details</div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm mb-4">
                    <div className="min-w-0"><div className="text-[var(--av-text-muted)] text-xs">Server ID</div><div className="font-mono break-all">{p.server_id}</div></div>
                    <div className="min-w-0"><div className="text-[var(--av-text-muted)] text-xs">Governor UID</div><div className="font-mono break-all">{p.governor_uid}</div></div>
                    <div className="min-w-0"><div className="text-[var(--av-text-muted)] text-xs">In-Game Name</div><div className="font-mono break-all">{p.in_game_name}</div></div>
                  </div>
                  <label className="flex items-start gap-2 text-sm">
                    <input type="checkbox" data-testid="offer-confirm-checkbox"
                            checked={confirmed}
                            onChange={e => setConfirmed(e.target.checked)}
                            className="mt-1 shrink-0"/>
                    <span>I have checked that my Server ID, Governor UID and In-Game Name are correct.</span>
                  </label>
                  <div className="mt-3 pt-3 border-t border-[var(--av-border)]/60 space-y-2.5">
                    <label className="flex items-start gap-2 text-sm">
                      <input type="checkbox" data-testid="offer-terms-checkbox"
                              checked={agreedTerms}
                              onChange={e => setAgreedTerms(e.target.checked)}
                              className="mt-1 shrink-0"/>
                      <span>
                        I agree to the{" "}
                        <a href="/legal/terms" target="_blank" rel="noopener noreferrer"
                            className="text-[var(--av-gold)] underline underline-offset-2">Terms &amp; Conditions</a>{" "}
                        and acknowledge the{" "}
                        <a href="/legal/refund" target="_blank" rel="noopener noreferrer"
                            className="text-[var(--av-gold)] underline underline-offset-2">Refund &amp; Order Issues Policy</a>.
                      </span>
                    </label>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Summary */}
          <div className="lg:col-span-2 min-w-0">
            <div className="vault-card p-6 lg:sticky lg:top-24 space-y-3 min-w-0">
              <div className="mono-caption">Order Summary</div>
              <div>
                <div className="text-xs text-[var(--av-text-dim)]">Offer</div>
                <div className="font-serif text-xl font-bold gold-text">{offerName}</div>
              </div>
              <div>
                <div className="text-xs text-[var(--av-text-dim)]">Total Apex</div>
                <div className="font-serif text-3xl font-bold gold-text" data-testid="offer-checkout-apex">
                  {offer.total_apex.toLocaleString()}
                </div>
                <div className="text-[11px] text-[var(--av-text-muted)]">
                  {offer.underlying_pack_quantity} × {offer.underlying_apex_amount.toLocaleString()} Apex Coin Packs
                </div>
              </div>
              <div className="divider-gold my-2"></div>
              <div>
                <div className="mono-caption">You Pay</div>
                <div className="flex items-baseline gap-3 flex-wrap">
                  <div className="font-serif text-3xl font-bold" data-testid="offer-checkout-price">{priceStr}</div>
                  {offer.normal_equivalent_price_gbp > offer.special_price_gbp && (
                    <div className="text-sm text-[var(--av-text-muted)] line-through decoration-[var(--av-purple)]/70">
                      {format(offer.normal_equivalent_price_gbp)}
                    </div>
                  )}
                </div>
                {!isBase && (
                  <div className="text-[11px] text-[var(--av-text-muted)] mt-1">
                    charged £{offer.special_price_gbp.toFixed(2)} GBP
                  </div>
                )}
              </div>
              <div className="text-[11px] text-[var(--av-text-dim)] flex items-start gap-1">
                <Sparkles className="w-3 h-3 text-[var(--av-purple)] mt-0.5 shrink-0"/>
                <span>Earn Apex Rewards at your normal loyalty rate. Existing Apex Rewards cannot be redeemed against special offers.</span>
              </div>
              <button
                data-testid="offer-checkout-pay"
                disabled={busy || salesPaused}
                onClick={pay}
                className="btn-gold w-full mt-3 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {salesPaused
                  ? "Sales Paused"
                  : busy
                    ? "Redirecting…"
                    : (<>Continue to Payment <ArrowRight className="w-4 h-4 inline ml-1"/></>)}
              </button>
              {salesPaused && (
                <div className="text-[11px] text-red-300 text-center mt-1" data-testid="offer-checkout-paused-note">
                  {pauseMessage || "Sales are paused right now — new orders can't be placed."}
                </div>
              )}
              <div className="text-xs text-[var(--av-text-dim)] flex justify-center items-center gap-1">
                <ShieldCheck className="w-3 h-3"/> Secure Stripe checkout · GBP
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer/>
    </div>
  );
}
